import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { playAcknowledgement } from "../audio";
import { formatGold } from "../format";
import { useEscape } from "../escape";
import { BusyLabel } from "./BusyLabel";
import { ErrorBox } from "./Shell";
import { STAKES, canAnswer, challengeable, stakeQuestion, stakesFor, wagerStatus, wagerTitle, type Wager } from "../../shared/wagers.ts";
import "./wagers.css";

/**
 * Wagers (docs/wagers.md, flag `wagerUI`): on Rewards, above the Purse, a bronze card listing this
 * peon's offered and open wagers and the last three finished, one short line each, with Accept and
 * Decline as ghost buttons on one they were challenged to. "Challenge a peon" opens the names (the
 * send-to-peon idiom, without Everyone); a name gives way to "How much against Hue?" and the stakes
 * this peon holds, and a stake sends the challenge. `refreshKey` (the Rewards
 * summary, new on every live change) reloads the list, so a settle or an answer elsewhere shows.
 */
export function WagersCard({ peonName, balance, refreshKey }: { peonName: string; balance: number; refreshKey: unknown }) {
  const [live, setLive] = useState<Wager[] | null>(null);
  const [past, setPast] = useState<Wager[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [names, setNames] = useState<string[] | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  /** The control whose request is in flight: a stake ("stake-20") or an answer ("accept-<id>"). The ref locks out a double tap. */
  const [busy, setBusy] = useState<string | null>(null);
  const busyRef = useRef(false);
  const [status, setStatusLine] = useState<{ text: string; error?: boolean } | null>(null);
  const setStatus = (text: string | null, err = false) => setStatusLine(text === null ? null : { text, error: err });
  const generation = useRef(0);

  async function load() {
    const mine = ++generation.current;
    try {
      const r = await api.wagers.list();
      if (mine !== generation.current) return;
      setLive(r.live);
      setPast(r.past);
      setError(null);
    } catch (e) {
      if (mine === generation.current) setError(e instanceof Error ? e.message : "Could not load wagers");
    }
  }

  useEffect(() => {
    void load();
  }, [refreshKey, peonName]);

  useEffect(() => {
    if (!choosing || names) return;
    api.peons.list().then(
      ({ names: all }) => setNames(all),
      () => setNames([]),
    );
  }, [choosing, names]);

  const openerRef = useRef<HTMLButtonElement>(null);
  useEscape(choosing, () => {
    setChoosing(false);
    setPicked(null);
    openerRef.current?.focus({ preventScroll: true });
  });

  async function run(key: string, work: () => Promise<void>) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(key);
    setStatus(null);
    try {
      await work();
    } catch (e) {
      setStatus(e instanceof Error ? e.message : "Something went wrong", true);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  }

  function offer(to: string, stake: number) {
    return run(`stake-${stake}`, async () => {
      void playAcknowledgement();
      const { wager } = await api.wagers.offer(to, stake);
      setLive((l) => [wager, ...(l ?? [])]);
      setChoosing(false);
      setPicked(null);
      setStatus(`Challenge sent to ${to}.`);
    });
  }

  function answer(w: Wager, action: "accept" | "decline") {
    if (action === "accept" && !confirm(`Put up ${formatGold(w.stake)} against ${w.challenger}?`)) return;
    return run(`${action}-${w.id}`, async () => {
      if (action === "accept") void playAcknowledgement();
      const { wager } = await api.wagers.answer(w.id, action);
      if (action === "accept") setLive((l) => (l ?? []).map((x) => (x.id === w.id ? wager : x)));
      else {
        setLive((l) => (l ?? []).filter((x) => x.id !== w.id));
        setPast((p) => [wager, ...p].slice(0, 3));
      }
    });
  }

  const now = Date.now();
  const stakes = stakesFor(balance);
  const others = names ? challengeable(names, peonName, live ?? [], now) : null;
  const rows = [...(live ?? []), ...past];

  return (
    <section class="wagers" aria-labelledby="wagers-heading">
      <h2 class="rewards-heading" id="wagers-heading">Wagers</h2>
      {error && <ErrorBox message={error} onRetry={live ? undefined : () => void load()} />}
      <div class="card wagers-card">
        {live && rows.length === 0 && <p class="muted small wagers-empty">No wagers yet.</p>}
        {rows.length > 0 && (
          <ul class="wagers-list">
            {rows.map((w) => (
              <li key={w.id} class="wager-row" data-wager-id={w.id}>
                <span class="wager-title">{wagerTitle(w)}</span>
                <span class="wager-status">{wagerStatus(w, peonName, now)}</span>
                {canAnswer(w, peonName, now) && (
                  <div class="wager-answer">
                    <button type="button" class="btn btn-ghost" disabled={busy !== null} onClick={() => void answer(w, "accept")}>
                      <BusyLabel busy={busy === `accept-${w.id}`} label="Accepting…">Accept</BusyLabel>
                    </button>
                    <button type="button" class="btn btn-ghost" disabled={busy !== null} onClick={() => void answer(w, "decline")}>
                      <BusyLabel busy={busy === `decline-${w.id}`} label="Declining…">Decline</BusyLabel>
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        <div class="wager-challenge">
          <button
            type="button"
            class="btn-quiet"
            ref={openerRef}
            disabled={stakes.length === 0}
            aria-expanded={choosing}
            onClick={() => {
              setChoosing((c) => !c);
              setPicked(null);
              setStatus(null);
            }}
          >
            Challenge a peon
          </button>
          {stakes.length === 0 && <p class="muted small wager-need">Need {formatGold(STAKES[0] - balance)} more</p>}
          {choosing && !picked && (
            <div class="wager-pick" role="group" aria-label="Challenge">
              {others === null && <span class="muted small">Finding peons…</span>}
              {others?.map((n) => (
                <button key={n} type="button" class="btn btn-ghost" disabled={busy !== null} onClick={() => setPicked(n)}>
                  {n}
                </button>
              ))}
              {others && others.length === 0 && <span class="muted small">Nobody free to challenge.</span>}
            </div>
          )}
          {choosing && picked && <p class="muted small wager-line">{stakeQuestion(picked)}</p>}
          {choosing && picked && (
            <div class="wager-pick" role="group" aria-label={stakeQuestion(picked)}>
              {stakes.map((s) => (
                <button key={s} type="button" class="btn btn-ghost" disabled={busy !== null} onClick={() => void offer(picked, s)}>
                  <BusyLabel busy={busy === `stake-${s}`} label="Sending…">{`${s} gold`}</BusyLabel>
                </button>
              ))}
            </div>
          )}
          {status &&
            (status.error ? (
              <p class="muted small wager-line" role="alert">{status.text}</p>
            ) : (
              <p class="muted small wager-line" role="status">{status.text}</p>
            ))}
        </div>
      </div>
    </section>
  );
}
